import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Target as TargetIcon,
  Plus,
  Search,
  Trash2,
  TrendingUp,
  AlertCircle,
  Sparkles,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Users as UsersIcon,
  MapPin,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { usePermissions } from '../hooks/usePermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (n ?? 0).toLocaleString('pt-BR');

const ProgressBar = ({ pct, accent = 'emerald' }) => {
  const accentMap = {
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
  };
  const tone =
    pct >= 100 ? 'emerald' : pct >= 60 ? 'emerald' : pct >= 30 ? 'amber' : 'rose';
  const width = Math.min(100, Math.max(0, pct || 0));
  return (
    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
      <div
        className={`h-full rounded-full ${accentMap[tone] || accentMap[accent]} transition-all duration-500`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
};

export default function MetasPage() {
  const { activeTenant } = useAuth();
  const { settings, refresh: refreshSettings } = useSettings();
  const { canEdit } = usePermissions();

  const cargo = settings?.cargo_objetivo || '';
  const sharePct = settings?.meta_share_pct || 20.0;
  const canEditMetas = canEdit('liderancas');

  const [cargos, setCargos] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState('cidade'); // 'cidade' or 'grupo'
  const [cities, setCities] = useState({}); // {UF: [{cidade, votos_validos}]}
  const [search, setSearch] = useState('');
  const [pickedCity, setPickedCity] = useState(null);          // for 'cidade'
  const [selectedCities, setSelectedCities] = useState([]);    // for 'grupo': [{estado, cidade, votos_validos}]
  const [groupName, setGroupName] = useState('');
  const [metaValue, setMetaValue] = useState('');
  const [savingNew, setSavingNew] = useState(false);

  // Set of expanded group rows
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const [dashRes, cargosRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/metas/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tse/cargos`, { withCredentials: true }),
      ]);
      setDashboard(dashRes.data);
      setCargos(cargosRes.data);
    } catch (e) {
      toast.error('Falha ao carregar dashboard de metas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCities = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tse/cities`, { withCredentials: true });
      setCities(data.states || {});
    } catch (e) {
      toast.error('Falha ao carregar cidades.');
    }
  };

  const openAdd = async () => {
    setAddMode('cidade');
    setPickedCity(null);
    setSelectedCities([]);
    setGroupName('');
    setMetaValue('');
    setSearch('');
    setAddOpen(true);
    await loadCities();
  };

  const filteredCities = useMemo(() => {
    const term = search
      .trim()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
    if (!term) return cities;
    const out = {};
    for (const [uf, list] of Object.entries(cities)) {
      const matches = list.filter((c) => c.cidade.includes(term));
      if (matches.length) out[uf] = matches;
    }
    return out;
  }, [cities, search]);

  const pickCity = (uf, item) => {
    setPickedCity({ estado: uf, cidade: item.cidade, votos_validos: item.votos_validos });
    setMetaValue(String(Math.round(item.votos_validos * (sharePct / 100))));
  };

  const toggleCityInGroup = (uf, item) => {
    setSelectedCities((prev) => {
      const exists = prev.findIndex((c) => c.estado === uf && c.cidade === item.cidade);
      let next;
      if (exists >= 0) {
        next = prev.filter((_, i) => i !== exists);
      } else {
        next = [...prev, { estado: uf, cidade: item.cidade, votos_validos: item.votos_validos }];
      }
      const totalVotos = next.reduce((s, c) => s + (c.votos_validos || 0), 0);
      setMetaValue(String(Math.round(totalVotos * (sharePct / 100))));
      return next;
    });
  };

  const isCitySelected = (uf, cityName) =>
    selectedCities.some((c) => c.estado === uf && c.cidade === cityName);

  const groupTotalVotos = useMemo(
    () => selectedCities.reduce((s, c) => s + (c.votos_validos || 0), 0),
    [selectedCities]
  );

  const saveNewMeta = async () => {
    setSavingNew(true);
    try {
      const body =
        addMode === 'cidade'
          ? {
              tipo: 'cidade',
              cidades: pickedCity ? [{ estado: pickedCity.estado, cidade: pickedCity.cidade }] : [],
              meta_apoiadores: parseInt(metaValue, 10) || 0,
            }
          : {
              tipo: 'grupo',
              nome: groupName.trim(),
              cidades: selectedCities.map((c) => ({ estado: c.estado, cidade: c.cidade })),
              meta_apoiadores: parseInt(metaValue, 10) || 0,
            };
      if (addMode === 'cidade' && !body.cidades.length) {
        toast.error('Selecione uma cidade.');
        return;
      }
      if (addMode === 'grupo' && (!body.nome || body.cidades.length < 2)) {
        toast.error('Grupos exigem um nome e ao menos 2 cidades.');
        return;
      }
      await axios.post(`${BACKEND_URL}/api/metas`, body, { withCredentials: true });
      toast.success(addMode === 'grupo' ? 'Grupo de cidades criado!' : 'Meta criada!');
      setAddOpen(false);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Falha ao criar meta';
      toast.error(detail);
    } finally {
      setSavingNew(false);
    }
  };

  const removeMeta = async (id) => {
    if (!window.confirm('Remover essa meta?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/metas/${id}`, { withCredentials: true });
      toast.success('Meta removida');
      refresh();
    } catch (e) {
      toast.error('Falha ao remover meta');
    }
  };

  const updateMetaInline = async (id, newValue) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/metas/${id}`,
        { meta_apoiadores: parseInt(newValue, 10) },
        { withCredentials: true }
      );
      refresh();
    } catch (e) {
      toast.error('Falha ao atualizar meta');
    }
  };

  // ---- empty states ----
  if (!cargo) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto py-12">
          <Card className="border-amber-200 bg-amber-50/40">
            <CardContent className="p-8 flex flex-col items-center text-center gap-4">
              <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-7 w-7 text-amber-700" />
              </div>
              <h2 className="text-2xl font-black tracking-tight text-slate-900">
                Configure o cargo objetivo da campanha
              </h2>
              <p className="text-sm text-slate-600 max-w-md leading-relaxed">
                Para usar Metas, você precisa primeiro definir <strong>qual cargo</strong> sua campanha está disputando (Prefeito, Vereador, Deputado Federal, etc.). É isso que determina os votos válidos de referência por cidade.
              </p>
              <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                <Link to="/configuracoes" data-testid="metas-configure-cta">
                  Ir para Configurações
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const cargoLabel = (cargos.find((c) => c.value === cargo) || {}).label || cargo;
  const cargoYear = (cargos.find((c) => c.value === cargo) || {}).ano_referencia;

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 font-outfit">
              Metas
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Metas de apoiadores por cidade — referência: <strong className="text-slate-700">{cargoLabel}</strong> ({cargoYear}) · share padrão <strong>{sharePct}%</strong>
            </p>
          </div>
          <Button
            onClick={openAdd}
            disabled={!canEditMetas}
            className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
            data-testid="metas-add-btn"
          >
            <Plus className="h-4 w-4" /> Adicionar cidade
          </Button>
        </div>

        {/* Overall KPI */}
        <Card className="border-slate-200 overflow-hidden">
          <CardContent className="p-0">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-200/80 flex items-center gap-1.5">
                <TargetIcon className="h-3 w-3" /> Meta geral da campanha
              </div>
              <div className="mt-3 flex items-baseline gap-3">
                <div className="text-5xl font-black tabular-nums font-outfit" data-testid="metas-total-atingido">
                  {fmt(dashboard?.total_atingido)}
                </div>
                <div className="text-2xl text-slate-300 tabular-nums">
                  / {fmt(dashboard?.total_meta)}
                </div>
                <Badge className="bg-emerald-500/20 text-emerald-100 border-emerald-400/30 ml-2">
                  {dashboard?.total_pct ?? 0}%
                </Badge>
              </div>
              <div className="mt-4">
                <div className="h-3 rounded-full bg-slate-700/50 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-700"
                    style={{ width: `${Math.min(100, dashboard?.total_pct ?? 0)}%` }}
                  />
                </div>
                <div className="text-xs text-slate-300 mt-2">
                  {dashboard && dashboard.total_meta > 0 ? (
                    <>
                      Faltam <strong className="text-white">{fmt(Math.max(0, (dashboard.total_meta || 0) - (dashboard.total_atingido || 0)))}</strong> apoiadores para bater a meta.
                    </>
                  ) : (
                    <>Adicione cidades abaixo para começar a acompanhar a meta.</>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Targets table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-sm text-slate-400 py-8 text-center">Carregando…</div>
            ) : !dashboard?.targets || dashboard.targets.length === 0 ? (
              <div className="py-12 text-center space-y-3">
                <Sparkles className="h-10 w-10 text-slate-300 mx-auto" />
                <p className="text-sm text-slate-500">
                  Nenhuma meta ainda. Adicione sua primeira cidade.
                </p>
                <Button
                  onClick={openAdd}
                  disabled={!canEditMetas}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
                >
                  <Plus className="h-4 w-4" /> Adicionar cidade
                </Button>
              </div>
            ) : (
              <Table data-testid="metas-table">
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Tipo</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Nome</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Votos válidos<br /><span className="font-normal normal-case text-slate-400">({cargoYear})</span></TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Meta</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Atingido</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 w-64">Progresso</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboard.targets.map((t) => {
                    const isGroup = t.tipo === 'grupo';
                    const isExpanded = expanded.has(t.id);
                    return (
                      <React.Fragment key={t.id}>
                        <TableRow data-testid={`meta-row-${t.id}`} className={isGroup ? 'bg-emerald-50/30' : ''}>
                          <TableCell>
                            {isGroup ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => toggleExpand(t.id)}
                                data-testid={`meta-expand-${t.id}`}
                                title="Ver cidades do grupo"
                              >
                                {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                              </Button>
                            ) : (
                              <span className="h-6 w-6 inline-block" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={isGroup
                                ? 'bg-emerald-100 border-emerald-300 text-emerald-800 text-[10px] font-bold'
                                : 'bg-slate-100 border-slate-200 text-slate-700 text-[10px]'}
                            >
                              {isGroup ? (
                                <><UsersIcon className="h-2.5 w-2.5 mr-1 inline" />grupo</>
                              ) : (
                                <><MapPin className="h-2.5 w-2.5 mr-1 inline" />cidade</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm font-medium text-slate-800">
                            {t.nome}
                            {isGroup && (
                              <span className="text-[11px] text-slate-500 ml-2 font-normal">
                                ({t.cidades?.length || 0} cidades)
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-right text-slate-600 tabular-nums">
                            {fmt(t.votos_validos_ref)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={t.meta_apoiadores}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10) || 0;
                                setDashboard({
                                  ...dashboard,
                                  targets: dashboard.targets.map((x) =>
                                    x.id === t.id ? { ...x, meta_apoiadores: v } : x
                                  ),
                                });
                              }}
                              onBlur={(e) => updateMetaInline(t.id, e.target.value)}
                              disabled={!canEditMetas}
                              className="h-8 w-28 text-right tabular-nums text-sm ml-auto"
                              data-testid={`meta-input-${t.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`text-sm font-bold tabular-nums ${t.atingido >= t.meta_apoiadores ? 'text-emerald-700' : 'text-slate-800'}`}>
                              {fmt(t.atingido)}
                            </span>
                            {t.atingido >= t.meta_apoiadores && t.meta_apoiadores > 0 && (
                              <CheckCircle2 className="inline-block h-3.5 w-3.5 text-emerald-600 ml-1" />
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <ProgressBar pct={t.pct} />
                              <div className="text-[11px] text-slate-500 tabular-nums">{t.pct}%</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-rose-600 hover:bg-rose-50"
                              onClick={() => removeMeta(t.id)}
                              disabled={!canEditMetas}
                              data-testid={`meta-delete-${t.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        {isGroup && isExpanded && (t.cidades || []).map((c, idx) => (
                          <TableRow key={`${t.id}-${idx}`} className="bg-slate-50/40 border-l-2 border-emerald-300">
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell className="text-xs text-slate-600 pl-6">
                              <span className="text-slate-400 mr-1">↳</span> {c.cidade}
                              <span className="ml-1.5 text-[10px] text-slate-400">/ {c.estado}</span>
                            </TableCell>
                            <TableCell className="text-xs text-right text-slate-500 tabular-nums">
                              {fmt(c.votos_validos)}
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell className="text-xs text-right text-slate-700 tabular-nums">
                              {fmt(c.atingido)}
                            </TableCell>
                            <TableCell colSpan={2}></TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add city Dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="add-meta-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-outfit">
                <Plus className="h-5 w-5 text-emerald-600" /> Adicionar meta
              </DialogTitle>
              <DialogDescription className="text-xs">
                Crie uma meta para 1 cidade ou agrupe várias cidades (ex: "Região Metropolitana"). Votos válidos vêm do TSE para {cargoLabel} ({cargoYear}); sugestão = share de {sharePct}%.
              </DialogDescription>
            </DialogHeader>

            <Tabs value={addMode} onValueChange={(v) => { setAddMode(v); setPickedCity(null); setSelectedCities([]); setMetaValue(''); }} className="flex-1 flex flex-col min-h-0">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="cidade" data-testid="add-mode-cidade" className="text-xs gap-1.5">
                  <MapPin className="h-3.5 w-3.5" /> Por cidade
                </TabsTrigger>
                <TabsTrigger value="grupo" data-testid="add-mode-grupo" className="text-xs gap-1.5">
                  <UsersIcon className="h-3.5 w-3.5" /> Agrupamento de cidades
                </TabsTrigger>
              </TabsList>

              {/* === MODE: single city === */}
              <TabsContent value="cidade" className="flex-1 flex flex-col min-h-0 mt-4">
                {!pickedCity ? (
                  <div className="flex-1 flex flex-col gap-3 min-h-0">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar cidade…"
                        className="pl-9"
                        autoFocus
                        data-testid="city-search-input"
                      />
                    </div>
                    <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg min-h-[280px]">
                      {Object.keys(filteredCities).length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500">Nenhuma cidade encontrada.</div>
                      ) : (
                        Object.entries(filteredCities).map(([uf, list]) => (
                          <div key={uf}>
                            <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 z-10">
                              {uf} — {list.length} {list.length === 1 ? 'cidade' : 'cidades'}
                            </div>
                            {list.map((c) => (
                              <button
                                key={`${uf}-${c.cidade}`}
                                type="button"
                                onClick={() => pickCity(uf, c)}
                                className="w-full text-left px-3 py-2 hover:bg-emerald-50/60 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-3 transition-colors"
                                data-testid={`city-option-${uf}-${c.cidade}`}
                              >
                                <span className="text-sm font-medium text-slate-800">{c.cidade}</span>
                                <span className="text-[11px] text-slate-500 tabular-nums">{fmt(c.votos_validos)} votos válidos</span>
                              </button>
                            ))}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 py-2">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Cidade selecionada</div>
                      <div className="text-xl font-black text-slate-900 mt-0.5">
                        {pickedCity.cidade} <span className="text-sm font-normal text-slate-500">/ {pickedCity.estado}</span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                        <div className="rounded-md bg-white ring-1 ring-slate-200 p-2">
                          <div className="text-slate-400 font-semibold uppercase text-[10px]">Votos válidos {cargoYear}</div>
                          <div className="text-lg font-black text-slate-900 tabular-nums">{fmt(pickedCity.votos_validos)}</div>
                        </div>
                        <div className="rounded-md bg-white ring-1 ring-slate-200 p-2">
                          <div className="text-slate-400 font-semibold uppercase text-[10px]">Sugestão ({sharePct}%)</div>
                          <div className="text-lg font-black text-emerald-700 tabular-nums">{fmt(Math.round(pickedCity.votos_validos * (sharePct / 100)))}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="meta_value" className="text-sm font-semibold">Sua meta de apoiadores para {pickedCity.cidade}</Label>
                      <Input id="meta_value" type="number" value={metaValue} onChange={(e) => setMetaValue(e.target.value)} className="mt-1.5" data-testid="meta-value-input" />
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* === MODE: group === */}
              <TabsContent value="grupo" className="flex-1 flex flex-col min-h-0 mt-4 space-y-3">
                <div>
                  <Label htmlFor="group_name" className="text-sm font-semibold">Nome do grupo</Label>
                  <Input
                    id="group_name"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Ex.: Região Metropolitana de Goiânia"
                    className="mt-1.5"
                    data-testid="group-name-input"
                  />
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar cidade…"
                    className="pl-9"
                    data-testid="group-city-search"
                  />
                </div>

                {selectedCities.length > 0 && (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-2.5 max-h-24 overflow-y-auto">
                    <div className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider mb-1">
                      Selecionadas ({selectedCities.length}) · {fmt(groupTotalVotos)} votos válidos · sugestão {fmt(Math.round(groupTotalVotos * (sharePct / 100)))}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedCities.map((c) => (
                        <Badge
                          key={`sel-${c.estado}-${c.cidade}`}
                          variant="outline"
                          className="bg-white text-[10px] cursor-pointer hover:bg-rose-50 hover:border-rose-200"
                          onClick={() => toggleCityInGroup(c.estado, c)}
                          title="Clique para remover"
                        >
                          {c.cidade}/{c.estado} ×
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg min-h-[200px]">
                  {Object.keys(filteredCities).length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-500">Nenhuma cidade encontrada.</div>
                  ) : (
                    Object.entries(filteredCities).map(([uf, list]) => (
                      <div key={uf}>
                        <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 z-10">
                          {uf} — {list.length} {list.length === 1 ? 'cidade' : 'cidades'}
                        </div>
                        {list.map((c) => {
                          const selected = isCitySelected(uf, c.cidade);
                          return (
                            <button
                              key={`g-${uf}-${c.cidade}`}
                              type="button"
                              onClick={() => toggleCityInGroup(uf, c)}
                              className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-3 transition-colors ${
                                selected ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-slate-50'
                              }`}
                              data-testid={`group-city-option-${uf}-${c.cidade}`}
                            >
                              <span className="flex items-center gap-2">
                                <span className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
                                  selected ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'
                                }`}>
                                  {selected && <CheckCircle2 className="h-3 w-3 text-white" />}
                                </span>
                                <span className="text-sm font-medium text-slate-800">{c.cidade}</span>
                              </span>
                              <span className="text-[11px] text-slate-500 tabular-nums">{fmt(c.votos_validos)} votos</span>
                            </button>
                          );
                        })}
                      </div>
                    ))
                  )}
                </div>

                {selectedCities.length >= 2 && (
                  <div>
                    <Label className="text-sm font-semibold">Meta de apoiadores do grupo</Label>
                    <Input
                      type="number"
                      value={metaValue}
                      onChange={(e) => setMetaValue(e.target.value)}
                      className="mt-1.5"
                      data-testid="group-meta-input"
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="border-t border-slate-100 pt-4">
              {addMode === 'cidade' && pickedCity && (
                <Button variant="outline" onClick={() => setPickedCity(null)}>← Voltar</Button>
              )}
              <Button variant="outline" onClick={() => setAddOpen(false)} disabled={savingNew}>Cancelar</Button>
              <Button
                onClick={saveNewMeta}
                disabled={
                  savingNew ||
                  !metaValue ||
                  (addMode === 'cidade' && !pickedCity) ||
                  (addMode === 'grupo' && (selectedCities.length < 2 || !groupName.trim()))
                }
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="meta-save-btn"
              >
                {savingNew ? 'Salvando…' : 'Salvar meta'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
